import React, {
    memo,
    useMemo,
    useState,
    useCallback,
    useEffect,
    forwardRef,
} from "react";
import {
    motion,
    AnimatePresence,
    useAnimation,
    useMotionValue,
} from "framer-motion";
import { BsChevronDown, BsChevronUp, BsArrowRightShort } from "react-icons/bs";
import { FaMinus as MinusIcon, FaPlus as PlusIcon } from "react-icons/fa";
import parse from "html-react-parser";
import {
    HiOutlineMail,
    HiOutlineChevronDown as DownIcon,
} from "react-icons/hi";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";
import { MdCheck } from "react-icons/md";
// import { MdTextsms } from "react-icons/md";
import message from "../../utils/message";
import anim from "../../utils/anim";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import GET from "../../utils/GET";
// import Logo from "../../img/logo.svg";
import Logo from "../../img/disco-logo.png";
import "./core-ui.css";
import { useRef } from "react";
import getImgUrl from "../../utils/getImgUrl";
import {
    EXTENSION_PREVIEW_SKIP_BTN_CLASS,
    HONEST_PUBLISHER_ID,
    HONEST_STAGING_PUBLISHER_ID,
    LUMIN_PUBLISHER_ID,
    MERIDIAN_PUBLISHER_ID,
    VYBES_PUBLISHER_ID,
    YUMI_PUBLISHER_ID,
} from "../../conf";
import renderExtensionOffer, {
    EXTENSION_OFFER_TOGGLE_CLASS,
} from "../../utils/extension-previews/renderExtensionOffer";
import renderExtensionReward from "../../utils/extension-previews/renderExtensionReward";
import renderExtensionSurvey from "../../utils/extension-previews/renderExtensionSurvey";
import renderExtensionCustomPost from "../../utils/extension-previews/renderExtensionCustomPost";
import accessibilityProvider from "../../utils/accessibilityProvider";

const self = GET("self");
const publisher = GET("publisher");

export const isHonestWidget =
    publisher === HONEST_STAGING_PUBLISHER_ID ||
    publisher === HONEST_PUBLISHER_ID;

const fullWidthCard =
    publisher === VYBES_PUBLISHER_ID ||
    publisher === YUMI_PUBLISHER_ID ||
    publisher === MERIDIAN_PUBLISHER_ID ||
    publisher === LUMIN_PUBLISHER_ID ||
    isHonestWidget;

export const Line = () => <div className="widget-line" />;
export const Badge = ({ children, ...props }) => (
    <motion.div className="core-badge" {...props}>
        {children}
    </motion.div>
);
export const Button = ({
    children,
    onClick,
    className = "",
    transparent = false,
    style,
    ...props
}) => (
    <motion.div
        className={`core-button ${className}`}
        onClick={onClick}
        style={
            transparent
                ? {
                      backgroundColor: "var(--bg-light)",
                      border: "1px solid var(--text-light)",
                      color: "var(--text)",
                      boxShadow: "0px 5.5px 12px rgba(0, 0, 0, 0.12)",
                      ...style,
                  }
                : { ...style }
        }
        {...props}
    >
        {children}
    </motion.div>
);

export const DismissButton = React.memo(
    ({ children, className = "", ...props }) => (
        <motion.div className={`core-dismiss-button ${className}`} {...props}>
            {children}
        </motion.div>
    )
);
export const ThemeButton = React.memo(
    ({
        className = "",
        iconOnly = false,
        iconStyle = {},
        icon = null,
        event = false,
        children,
        link = "",
        ...props
    }) => {
        const track = useTrack();

        const handleEvent = () => {
            if (event) {
                track(event, { link });
            }
        };

        const linkProp = {};
        if (link) {
            linkProp.href = link;
        }

        return (
            <button
                className={`theme-button ${
                    iconOnly ? "theme-button-icon-only" : ""
                } ${className}`}
                onClick={handleEvent}
                {...props}
            >
                <a target="_blank" rel="noopener noreferrer" {...linkProp}>
                    {icon && (
                        <span style={iconStyle} className="theme-button-icon">
                            {icon}
                        </span>
                    )}
                    {children}
                </a>
            </button>
        );
    }
);

// ----------- Platform -------------
export const Timer = ({
    duration,
    start = true,
    onFinish,
    repeat = false,
    className = "",
    animated = false,
    pause = false,
    valueOnly = false,
    ...props
}) => {
    const [time, setTime] = useState(0);
    const timeAnimator = useAnimation();
    const regulator = useRef(null);
    const pauseTime = useRef(-1);

    const initTimer = useCallback(() => {
        setTime(duration);
        if (animated) {
            timeAnimator.set({ width: "0%" });
            timeAnimator.start({
                width: "100%",
                transition: {
                    type: "linear",
                    duration,
                },
            });
        }
        regulator.current = setInterval(() => {
            setTime((time) => time - 1);
        }, 1000);
    }, [duration, timeAnimator, animated]);

    const cleanUp = useCallback(() => {
        clearInterval(regulator.current);
    }, []);

    // Non-conventional solution for pausing timer
    useEffect(() => {
        if (pause && pauseTime.current === -1) {
            pauseTime.current = Math.max(time, 0);
            setTime(10000000);
        } else if (!pause && pauseTime.current !== -1) {
            setTime(pauseTime.current);
            pauseTime.current = -1;
        }
    }, [pause, time]);

    useEffect(() => {
        return () => {
            cleanUp();
        };
    }, [cleanUp]);

    useEffect(() => {
        if (!start) {
            return;
        }
        setTime(duration);
        initTimer();
    }, [setTime, duration, initTimer, start]);

    useEffect(() => {
        if (time < 0) {
            clearInterval(regulator.current);
            return !repeat ? onFinish() : initTimer();
        }
    }, [time, onFinish, initTimer, repeat]);

    if (valueOnly) {
        return pause && pauseTime.current !== -1
            ? pauseTime.current
            : Math.max(time, 0);
    }
    return (
        <>
            {!animated && (
                <div className="core-timer core-num-timer" {...props}>
                    Offer expires in{" "}
                    <span>
                        {pause && pauseTime.current !== -1
                            ? pauseTime.current
                            : Math.max(time, 0)}
                    </span>{" "}
                    seconds
                </div>
            )}
            {animated && (
                <div className="core-timer core-animated-timer" {...props}>
                    <div className="core-animated-timer-tube">
                        <motion.div
                            className="core-animated-timer-liq"
                            initial={{ width: "0%" }}
                            animate={timeAnimator}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

// ------------------------------------------

export const StatusLabel = React.memo(function ({
    children,
    type = "error",
    className = "",
    ...props
}) {
    return (
        <motion.div
            className={`core-status-label ${
                type === "success"
                    ? "core-status-label-success"
                    : type === "theme"
                    ? "core-status-label-theme"
                    : ""
            } ${className}`}
            {...props}
        >
            {children}
        </motion.div>
    );
});

const CardOption = React.memo(
    ({ option, optionID, onClick, selected, handleInput }) => {
        const { icon_url, text, icon, text_input, follow_text } = option;
        return (
            <motion.div
                className={`card-option ${
                    text_input && selected ? "card-option-other" : ""
                }`}
                onClick={() => onClick(optionID)}
                variants={anim.cardOption}
            >
                {!selected &&
                    (icon || icon_url) &&
                    (icon ? (
                        <span className="card-option-img card-option-icon-img">
                            {icon_url}
                        </span>
                    ) : (
                        <img
                            src={icon_url}
                            className="card-option-img"
                            alt="Option Icon"
                        />
                    ))}
                {!selected && <div className="card-option-text">{text}</div>}
                <AnimatePresence>
                    {text_input && selected && (
                        <Input
                            small
                            placeholder={
                                follow_text
                                    ? follow_text
                                    : `${text}: Please enter your response`
                            }
                            onClick={handleInput}
                        />
                    )}
                </AnimatePresence>
            </motion.div>
        );
    }
);

const HeaderCTABtn = ({ text, icon, onClick }) => {
    return (
        <div className="static-card-cta-button" onClick={onClick}>
            <span>{icon}</span>
            <span>{text}</span>
            <FaChevronRight />
        </div>
    );
};

export const Card = ({
    heading,
    completion,
    options,
    handleParent,
    isDefault,
    modalAnswer,
    handleIntermediateResponse,
    ...props
}) => {
    const [open, setOpen] = useState(true);
    const [answer, setAnswer] = useState(-1);
    const [otherAnswerVal, setOtherAnswerVal] = useState("");

    const handleAnswer = useCallback(
        (key) => {
            if (answer !== key) {
                setAnswer(key);
                const textOption = options[key].text_input;
                setOpen(textOption);
                if (!textOption) {
                    return handleParent(key);
                }
                if (
                    textOption &&
                    typeof handleIntermediateResponse === "function"
                ) {
                    handleIntermediateResponse(key);
                }
            }
        },
        [handleParent, options, answer, handleIntermediateResponse]
    );

    const handleOtherResponse = useCallback(
        (val) => {
            setOpen(false);
            setOtherAnswerVal(val);
            handleParent(answer, val);
        },
        [answer, handleParent]
    );

    useEffect(() => {
        if (modalAnswer > -1) {
            setAnswer(modalAnswer);
            setOpen(false);
            setOtherAnswerVal("");
            handleParent(modalAnswer, "", false);
        }
    }, [modalAnswer, options, handleParent]);

    return (
        <motion.section
            className={`card-root ${!open ? "card-root-complete" : ""} ${
                fullWidthCard ? "card-root-full-width" : ""
            }`}
            {...props}
        >
            <div className={`card-head ${!open ? "card-head-complete" : ""}`}>
                <div className="card-head-content">
                    {open
                        ? heading
                        : isDefault
                        ? `${completion}${
                              completion !== ""
                                  ? ` ${options[answer].extra_text || ""}`
                                  : ""
                          }`
                        : ""}
                    {answer >= 0 && (
                        <span
                            className="card-head-answer"
                            style={
                                !isDefault
                                    ? {
                                          fontSize: "1.2rem",
                                      }
                                    : {}
                            }
                        >
                            {options[answer].icon ? (
                                <span className="card-head-image">
                                    {options[answer].icon_url}
                                </span>
                            ) : (
                                <img
                                    src={options[answer].icon_url}
                                    className="card-head-image"
                                    alt="Icon"
                                />
                            )}
                            {options[answer].text_input
                                ? otherAnswerVal.length === 0
                                    ? options[answer].text
                                    : otherAnswerVal
                                : options[answer].text}
                        </span>
                    )}
                </div>
                <div className="card-head-arrow">
                    {open ? <BsChevronDown /> : <BsChevronUp />}
                </div>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={anim.cardBody}
                        className="card-body"
                        onAnimationComplete={() => message()}
                    >
                        {options.map((option, key) => {
                            return (
                                <CardOption
                                    key={option.text}
                                    option={option}
                                    optionID={key}
                                    onClick={handleAnswer}
                                    selected={key === answer}
                                    handleInput={handleOtherResponse}
                                />
                            );
                        })}
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.section>
    );
};

export const StaticCard = ({
    heading,
    closedHeading,
    children,
    cta = false,
    displayCoop = false,
    footer = false,
    closed = false,
    onClick = () => {},
    onClickSecondary = () => {},
    expandClick = () => {},
    maxHeight = null,
    light = false,
    animate = false,
    displayHeader = true,
    modal = false,
    className = "",
    ...props
}) => {
    const track = useTrack();

    const redirect = () => {
        track(eventTypes.WIDGET_COOP_LOGO_CLICK, {});
        const coopWindow = window.open();
        coopWindow.opener = null;
        coopWindow.location = `https://www.shopdisco.com/?utm_source=widget&utm_medium=web&utm_campaign=${
            GET("publisher") || "web"
        }${GET("email") ? `&email=${GET("email")}` : ""}${
            GET("publisher") ? `&publisher_id=${GET("publisher")}` : ""
        }${GET("name") ? `&name=${GET("name")}` : ""}`;
    };
    return (
        <motion.section
            className={`card-root ${
                fullWidthCard ? "card-root-full-width" : ""
            } ${className}`}
            variants={animate ? anim.staticCard : {}}
            initial="initial"
            animate="animate"
            exit="exit"
            style={modal ? { marginTop: "0px" } : {}}
        >
            {displayHeader && (
                <div
                    className={`
						card-head static-card-head
				x		${light ? "static-card-head-light" : ""}
						${!cta ? "static-card-head-padded" : ""}
						`}
                >
                    <div className="static-card-head-title-container">
                        <div className={`static-card-head-title`}>
                            {!closed ? heading : closedHeading}
                        </div>
                    </div>
                    {displayCoop && (
                        <div
                            className="static-card-head-coop"
                            onClick={redirect}
                            {...accessibilityProvider({
                                name: "powered by Disco",
                            })}
                        >
                            Powered by
                            <img src={Logo} alt="Disco Technology" />
                        </div>
                    )}
                </div>
            )}
            {cta && !self && (
                <>
                    <div className="static-card-cta-container">
                        <HeaderCTABtn
                            text="Send to Email"
                            icon={<HiOutlineMail />}
                            onClick={onClick}
                        />
                        {/* <HeaderCTABtn
							text="Send to Text"
							icon={<MdTextsms />}
							onClick={onClickSecondary}
						/> */}
                    </div>
                </>
            )}
            {children && !closed && (
                <motion.div
                    className="static-card-body"
                    variants={!animate ? anim.cardBody : {}}
                    initial="initial"
                    animate="animate"
                    style={maxHeight ? { maxHeight: `${maxHeight}px` } : {}}
                    {...props}
                >
                    {children}
                </motion.div>
            )}
            {footer && (
                <div
                    className="static-card-footer"
                    onClick={expandClick}
                    {...accessibilityProvider({
                        name: `See More Products`,
                    })}
                >
                    <div className="static-card-footer-button">
                        <span>See More Products</span>
                        <FaChevronDown />
                    </div>
                </div>
            )}
        </motion.section>
    );
};

export const Loader = React.memo(
    ({ center = false, normal = false, light = false, className = "" }) => {
        const height = normal ? false : GET("innerHeight");

        return (
            <div
                className={`loader ${center ? "loader-center" : ""} ${
                    light ? "loader-light" : ""
                } ${className}`}
                style={{
                    bottom: height ? "initial" : "0",
                    top: height ? height / 2 - 10 + "px" : "0",
                }}
            >
                <div className="loader-circle" />
                <div className="loader-circle" />
            </div>
        );
    }
);

export const Input = ({
    icon,
    buttonType = "button",
    small = false,
    className = "",
    classNameInput = "",
    onClick = () => {},
    children,
    ...props
}) => {
    const [inputVal, setInputVal] = useState("");
    const [showButton, setButton] = useState(false);
    const handleChange = (e) => {
        const val = e.target.value;
        val.length > 0 ? setButton(true) : setButton(false);
        setInputVal(e.target.value);
    };
    return (
        <motion.div
            className={`input-container ${
                small ? "input-container-small" : ""
            } ${className}`}
            variants={anim.inputContainer}
            initial="initial"
            animate="animate"
            exit="exit"
        >
            {icon && <span className="input-icon">{icon}</span>}
            <input
                className={`input ${classNameInput}`}
                value={inputVal}
                onChange={handleChange}
                {...props}
            />
            {children}
            <AnimatePresence>
                {showButton && (
                    <motion.button
                        type={buttonType}
                        className="input-button"
                        variants={anim.button}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        onClick={() => {
                            onClick(inputVal);
                        }}
                    >
                        <BsArrowRightShort />
                    </motion.button>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export const BlurOverlay = React.memo(
    ({ children, className, disableAnimation = false, ...props }) => {
        return (
            <motion.section
                className={`blur-overlay-container ${className}`}
                variants={disableAnimation ? anim.dialogQuick : anim.dialog}
                initial={disableAnimation ? "" : "initial"}
                animate="animate"
                exit="exit"
                {...props}
            >
                {children}
            </motion.section>
        );
    }
);

const GESTURE_BREAKPOINT = 150;
export const WidgetModal = React.memo(
    ({
        children,
        className = "",
        data,
        offset,
        title = "",
        displayFooter = false,
        hideBrand = false,
        hideBrandLogo = false,
        animateConditionally = true,
        overflow = false,
        triggerClose,
        sendModalData,
        identifier,
        preview = false,
        ...props
    }) => {
        const customColor =
            data &&
            data.publisher_details &&
            data.publisher_details.modal_color;
        const { p: publisherName, publisher_details: publisherDetails } = data;
        // } = !preview ? data : {};
        const widgetModalRef = useRef(null);
        const animationControl = useAnimation();
        const rotation = useMotionValue(0);
        const opacity = useMotionValue(1);
        const overlayOpacity = useMotionValue(0);
        const [dragActive, setDragActive] = useState(false);
        const closed = useRef(false);
        // toggle this to allow or not allow rotation
        const useRotation = false;

        const [cardHeight, setCardHeight] = useState(null);

        useEffect(() => {
            if (props.active || !animateConditionally) {
                animationControl.start("animate");
            }
        }, [props.active, animateConditionally, animationControl]);

        useEffect(() => {
            // 	widgetModalRef.current.offsetLeft,
            // 	widgetModalRef.current.offsetWidth
            opacity.onChange((latest) => {
                if (sendModalData && typeof sendModalData === "function") {
                    sendModalData({
                        identifier,
                        opacity: latest,
                    });
                }
            });
        }, [sendModalData, opacity, identifier]);

        const handleDrag = useCallback(
            (_, info) => {
                setDragActive(true);
                if (
                    Math.abs(info.offset.x) >= GESTURE_BREAKPOINT &&
                    dragActive &&
                    opacity.current < 0.7 &&
                    !closed.current
                ) {
                    closed.current = true;
                    return triggerClose(identifier, true);
                }
                const grad =
                    Math.abs(info.offset.x) / (GESTURE_BREAKPOINT + 40);
                opacity.set(1 - grad);
                overlayOpacity.set(grad / 1.3);

                const angle = Math.asin(
                    info.offset.x / widgetModalRef.current.offsetHeight
                );
                if (!isNaN(angle) && useRotation) {
                    rotation.set(angle * -20);
                }
            },
            [
                opacity,
                triggerClose,
                identifier,
                overlayOpacity,
                dragActive,
                rotation,
                useRotation,
            ]
        );

        const handleDragEnd = useCallback(
            (_, info) => {
                setDragActive(false);
                if (
                    Math.abs(info.offset.x) >= GESTURE_BREAKPOINT &&
                    dragActive &&
                    opacity.current < 0.7 &&
                    !closed.current
                ) {
                    closed.current = true;
                    return triggerClose(identifier, true);
                }
                opacity.set(1);
                rotation.set(0);
                overlayOpacity.set(0);
            },
            [
                opacity,
                identifier,
                triggerClose,
                overlayOpacity,
                dragActive,
                rotation,
            ]
        );

        const height =
            GET("innerHeight") || (GET("full") && window.innerHeight) || null;
        const heightBuffer = GET("platform") ? 0 : 40;

        const optimizedStyle =
            height && height < 700
                ? {
                      image: {
                          marginTop: "-10px",
                          width: "68px",
                          height: "68px",
                          padding: " 4px",
                      },
                      title: {
                          marginTop: "6px",
                      },
                      subTitle: {
                          marginTop: "6px",
                      },
                      body: {
                          marginTop: "6px",
                      },
                  }
                : {
                      image: {},
                      title: {},
                      subTitle: {},
                      body: {},
                  };

        useEffect(() => {
            setCardHeight(
                1.073 * widgetModalRef?.current?.getBoundingClientRect().height
            );
        }, []);

        return (
            <>
                <motion.section
                    className={`widget-modal-container ${className}`}
                    custom={{
                        identifier,
                        length:
                            (data &&
                                data.display_order &&
                                data.display_order.length) ||
                            1,
                    }}
                    ref={widgetModalRef}
                    variants={anim.modalContainer}
                    initial="initial"
                    animate={animationControl}
                    exit="exit"
                    style={{
                        rotate: rotation,
                        opacity,
                        marginTop: `${Math.min(
                            height && cardHeight
                                ? (height - heightBuffer - cardHeight) / 2
                                : height
                                ? 0.03 * (height - heightBuffer)
                                : offset,
                            offset
                        )}px`,
                        // marginTop: `${Math.min(
                        // 	offset,
                        // 	height ? 0.03 * height : offset
                        // )}px`,
                        maxHeight: height
                            ? `${0.96 * (height - heightBuffer)}px`
                            : "auto",
                    }}
                    drag={props.active ? "x" : false}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    dragElastic={0}
                    dragTransition={{ bounceStiffness: 900, bounceDamping: 20 }}
                    whileTap={{ cursor: "grabbing" }}
                    layout
                >
                    {!hideBrandLogo && (
                        <motion.img
                            src={publisherDetails.logo_url}
                            className="widget-modal-image"
                            alt="brand logo"
                            variants={anim.dialogItem}
                            style={optimizedStyle.image}
                        />
                    )}
                    {!hideBrand && (
                        <>
                            <motion.div
                                className="widget-modal-title"
                                variants={anim.dialogItem}
                                style={optimizedStyle.title}
                            >
                                {publisherName}
                            </motion.div>
                            <motion.div
                                className="widget-modal-subtitle"
                                variants={anim.dialogItem}
                                style={optimizedStyle.subTitle}
                            >
                                {publisherDetails.single_liner}
                            </motion.div>
                        </>
                    )}
                    {hideBrand && title !== "" && (
                        <motion.div
                            className="widget-modal-title widget-modal-title-custom"
                            variants={anim.dialogItem}
                        >
                            {title}
                        </motion.div>
                    )}
                    <motion.div
                        className="widget-modal-body"
                        variants={anim.dialogItem}
                        style={optimizedStyle.body}
                    >
                        {children}
                    </motion.div>
                    <motion.div
                        className="widget-modal-overlay"
                        style={{
                            opacity: overlayOpacity,
                            backgroundColor: customColor ? customColor : "auto",
                            display: dragActive ? "block" : "none",
                        }}
                    />
                </motion.section>
                {displayFooter && (
                    <motion.div className="widget-modal-footer">
                        <motion.div
                            className="widget-modal-footer-tagline"
                            variants={anim.dialogItem}
                        >
                            Custom survey powered by
                            <div className="widget-modal-footer-image">
                                <img src={Logo} alt="coop" />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </>
        );
    }
);

const BrandBubble = React.memo(
    ({
        center = false,
        imgSrc = "",
        dataArray,
        index,
        zindex,
        scale = 1,
        right = false,
        left = false,
        top = false,
        noAnim = false,
        className = "",
        ...props
    }) => {
        const [, setDisplay] = useState(true);
        useEffect(() => {
            if (center || index < dataArray.length) {
                return;
            }
            setDisplay(false);
        }, [center, index, dataArray, setDisplay]);
        return (
            <motion.img
                src={
                    center
                        ? imgSrc
                        : index < dataArray.length
                        ? dataArray[index].logo_url
                        : Logo
                }
                alt=""
                variants={
                    noAnim
                        ? null
                        : center
                        ? anim.bubble
                        : top
                        ? anim.bubbleTop
                        : anim.bubbleBottom
                }
                initial="initial"
                animate="animate"
                custom={{ scale, index }}
                className={`core-brand-bubble ${
                    left ? "core-brand-bubble-left" : ""
                } ${right ? "core-brand-bubble-right" : ""} ${className}`}
                style={{
                    zIndex: center ? 100 : zindex,
                    transform: `scale(${scale})`,
                    animationDelay: `${Math.random() * 6 + 2}s`,
                    animationName: center ? "pulse" : "",
                    animationDuration: center
                        ? "6s"
                        : `${Math.random() * 6 + 4}s`,
                }}
                {...props}
            />
        );
    }
);

export const BrandBubbles = React.memo(({ modalData, data, ...props }) => {
    return (
        <motion.section
            className="core-brand-bubble-container"
            {...props}
            variants={anim.bubbleContainer}
            initial="initial"
            animate="animate"
        >
            <div className="core-brand-bubble-pre">
                <BrandBubble
                    dataArray={modalData.master_brands}
                    index={4}
                    top
                />
                <div
                    className="core-brand-bubble-center-row"
                    style={{ transform: "translateY(-16px)" }}
                >
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={8}
                        right
                    />
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={6}
                    />
                </div>
            </div>
            <div className="core-brand-bubble-center">
                <div className="core-brand-bubble-center-row">
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={2}
                        zindex={1}
                        scale={0.8}
                        top
                    />
                    <BrandBubble
                        center
                        imgSrc={data.publisher_details.logo_url}
                        scale={1.6}
                    />
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={3}
                        scale={0.8}
                        top
                    />
                </div>
                <div
                    className="core-brand-bubble-center-row"
                    style={{ transform: "translateY(-20px)", zIndex: 2 }}
                >
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={0}
                    />
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={1}
                    />
                </div>
            </div>
            <div className="core-brand-bubble-post">
                <BrandBubble
                    dataArray={modalData.master_brands}
                    index={5}
                    top
                />
                <div
                    className="core-brand-bubble-center-row"
                    style={{ transform: "translateY(-16px)" }}
                >
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={7}
                    />
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={9}
                        left
                    />
                </div>
            </div>
        </motion.section>
    );
});

export const BrandBubblesLinear = React.memo(
    ({ modalData, data, ...props }) => {
        return (
            <section className="core-brand-bubble-linear-container" {...props}>
                <div className="core-brand-bubble-row">
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={2}
                        top
                        scale={0.8}
                        zindex={10}
                        noAnim
                        className="core-brand-bubble-linear-edge"
                    />
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={1}
                        top
                        scale={1.2}
                        zindex={11}
                        noAnim
                    />
                    <BrandBubble
                        center
                        imgSrc={data.publisher_details.logo_url}
                        scale={1.6}
                        zindex={1100}
                        // center
                        noAnim
                    />

                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={0}
                        scale={1.2}
                        zindex={11}
                        noAnim
                        top
                    />
                    <BrandBubble
                        dataArray={modalData.master_brands}
                        index={3}
                        top
                        scale={0.8}
                        zindex={10}
                        noAnim
                        className="core-brand-bubble-linear-edge-right"
                    />
                    <BrandBubble
                        center
                        imgSrc={data.publisher_details.logo_url}
                        scale={1.6}
                        zindex={1100}
                        className="core-brand-bubble-linear-main"
                        // center
                        noAnim
                    />
                </div>
                <section className="core-brand-linear-body"></section>
            </section>
        );
    }
);

export const Label = memo(
    ({ className = "", children, required, ...props }) => (
        <label className={`label ${className}`}>
            {required && <span className="label-required">* </span>}
            {children}
        </label>
    )
);

export const TabSwitcher = memo(
    ({ label = "", className = "", tabActive, data, onChange, ...props }) => {
        const parentRef = useRef();
        const parentRefProp = useRef();
        const [style, setStyle] = useState({
            left: "-2px",
        });

        const handleTabClick = (tabIndex) => {
            if (typeof onChange === "function") {
                onChange(tabIndex);
            }
        };

        useEffect(() => {
            if (!parentRef || !parentRef.current) {
                return;
            }
            parentRefProp.current = parentRef.current.getBoundingClientRect();
            setStyle((style) => ({
                ...style,
                left: `${tabActive * (100 / data.length) - 1}%`,
                width: `${(100 + 2.5 * data.length) / data.length}%`,
            }));
        }, [parentRef, data, tabActive]);

        return (
            <section className={`tab-switcher-container ${className}`}>
                <Label>{label}</Label>
                <section
                    className="tab-switcher-background"
                    ref={parentRef}
                    {...props}
                >
                    <div className="tab-switcher-switch" style={style} />
                    {data.map((dataItem, index) => {
                        const selfRef = React.createRef();
                        const handleClick = () => {
                            handleTabClick(index);
                        };
                        return (
                            <div
                                className="tab-switcher-tab"
                                ref={selfRef}
                                {...dataItem.props}
                                key={dataItem.name}
                                onClick={handleClick}
                            >
                                {dataItem.name}
                            </div>
                        );
                    })}
                </section>
            </section>
        );
    }
);

// Select Dropdown
export const Select = memo(
    React.forwardRef(
        (
            {
                data,
                options,
                value,
                onChange,
                name,
                onFocus,
                onOpen,
                onClose,
                emptyMessage,
                required,
                placeholder = "",
                className = "",
                style,
                searchable = false,
                searchOnly = false,
                onMouseEnter,
                multiple,
                loading,
                nested = false,
                expandable = false,
                independantExpandable = false,
                mapExpandableAreaProps,
                renderExpandableArea,
                empty = true,
                onHover,
                label,
                variantSelector = false,
                variantStock,
            },
            ref
        ) => {
            const [open, setOpen] = useState(false);
            const [highlightedKey, setHighlightedKey] = useState(-1);
            const selectRef = useRef(null);
            const searchRef = useRef(null);
            const [search, setSearch] = useState("");
            const [error, setError] = useState("");

            const uid = useRef("select-" + new Date().getTime()).current;

            const handleChange = (value, keyIndex) => {
                if (variantSelector && variantStock && variantStock[keyIndex]) {
                    return;
                }
                onChange({ target: { name, value } });
                setOpen(multiple);
                if (!multiple) {
                    setError("");
                }
            };

            const flatOptions = useMemo(
                () =>
                    nested
                        ? Object.entries(options).reduce(
                              (flat, [, sub]) => ({ ...flat, ...sub }),
                              {}
                          )
                        : options,
                [options, nested]
            );

            const canClose = useCallback(() => {
                if (!nested || !required) {
                    return empty || (value && value.length > 0);
                }

                const done = Object.entries(options).reduce(
                    (doneMap, [key]) => ({ ...doneMap, [key]: false }),
                    {}
                );

                value.forEach((id) => {
                    const parKey = Object.entries(options).find(
                        ([, val]) =>
                            Object.entries(val).findIndex(
                                ([innerId]) => innerId === id
                            ) !== -1
                    );
                    if (!parKey) {
                        return;
                    }
                    done[parKey[0]] = true;
                });

                return Object.entries(done).reduce(
                    (ret, [, val]) => ret && val,
                    true
                );
            }, [nested, options, value, empty, required]);

            const handleToggle = (e, force) => {
                // if (e) {
                // 	e.stopPropagation();
                // }

                setOpen((open) => {
                    const newOpen = force !== undefined ? force : !open;
                    if (newOpen) {
                        if (onOpen && typeof onOpen === "function") {
                            onOpen({ target: { name } });
                        }
                        if (searchRef.current) {
                            searchRef.current.focus();
                        }
                    } else if (!newOpen) {
                        if (!canClose()) {
                            setError(
                                nested
                                    ? "Please select at least one subcategory from each category"
                                    : "Please select at least one"
                            );
                            return true;
                        }
                        if (onClose && typeof onClose === "function") {
                            onClose({ target: { name, away: false } });
                        }
                        setSearch("");
                        setError("");
                    }
                    return newOpen;
                });
            };

            useEffect(() => {
                const handleClickAway = (e) => {
                    if (e.target.closest("#" + uid)) {
                        return e.preventDefault();
                    }
                    if (open) {
                        return setOpen(false);
                    }
                };
                document.addEventListener("click", handleClickAway);
                return () =>
                    document.removeEventListener("click", handleClickAway);
            }, [open, uid, onClose, name]);

            const handleOptionMouseEnter = useCallback(
                (key) => {
                    if (onHover && typeof onHover === "function") {
                        onHover(data.current, key);
                    }
                    setHighlightedKey(key);
                },
                [data, onHover]
            );
            const renderOptions = (options, search, extraClass = "") => {
                return Object.entries(options)
                    .filter(([key, text]) =>
                        search.trim()
                            ? text
                                  .trim()
                                  .toLowerCase()
                                  .indexOf(search.toLowerCase()) !== -1
                            : !searchOnly
                    )
                    .map(([key, text], optionID) => (
                        <section
                            className={`select-option ${extraClass} ${
                                variantSelector &&
                                variantStock &&
                                variantStock[optionID]
                                    ? "select-option-tall"
                                    : ""
                            }`}
                            onClick={() => handleChange(key, optionID)}
                            onMouseEnter={() => handleOptionMouseEnter(key)}
                            // variants={anim.selectOption}
                            key={key}
                        >
                            {multiple && value.indexOf(key) !== -1 && (
                                <span>
                                    <MdCheck />
                                </span>
                            )}
                            {text}
                            {variantSelector &&
                                variantStock &&
                                variantStock[optionID] && (
                                    <span className="select-option-after">
                                        <i>Out of Stock</i>
                                    </span>
                                )}
                        </section>
                    ));
            };

            // const [catOpen, setCatOpen] = useState(
            // 	Array(Object.entries(options).length)
            // );

            // const renderNestedOptions = (options, search) => {
            // 	return Object.entries(options).map(([key, subOptions], i) => (
            // 		<Collapsable
            // 			// variants={anim.selectOption}
            // 			className="select-option-group"
            // 			key={key}
            // 			open={catOpen[i]}
            // 			heading={
            // 				<>
            // 					{Object.entries(subOptions).reduce(
            // 						(found, [opt]) => {
            // 							return (
            // 								found || value.indexOf(opt) !== -1
            // 							);
            // 						},
            // 						false
            // 					) && (
            // 						<span className="select-option-group-icon">
            // 							<MdCheck />
            // 						</span>
            // 					)}
            // 					{key}
            // 				</>
            // 			}
            // 			setOpen={(fn) => {
            // 				setCatOpen((catOpen) => [
            // 					...catOpen.slice(0, i),
            // 					fn(catOpen[i], ...catOpen.slice(fn(i + 1))),
            // 				]);
            // 			}}
            // 		>
            // 			{renderOptions(subOptions, search)}
            // 		</Collapsable>
            // 	));
            // };

            const handleFocus = () => {
                if (typeof onFocus === "function") {
                    onFocus({ target: { name } });
                }
            };

            const handleMouseEnter = () => {
                if (typeof onMouseEnter === "function") {
                    onMouseEnter({ target: { name } });
                }
            };

            const [showValue, hasValue] = useMemo(() => {
                let selected = 0;
                const res =
                    multiple && value.length > 0
                        ? value
                              .filter((key) => !!flatOptions[key])
                              .map((key) => {
                                  selected++;
                                  return (
                                      <Badge key={flatOptions[key]} listItem>
                                          {flatOptions[key]}
                                      </Badge>
                                  );
                              })
                        : flatOptions[value];
                return [res, multiple ? selected : flatOptions[value]];
            }, [value, multiple, flatOptions]);

            return (
                <>
                    {label && <Label className="select-label">{label}</Label>}
                    {error && open && (
                        <StatusLabel className="select-error">
                            {error}{" "}
                        </StatusLabel>
                    )}
                    <section
                        ref={selectRef}
                        id={uid}
                        className={`select ${
                            options[value] || value.length ? "" : "select-empty"
                        } ${
                            expandable ? "select-expandable" : ""
                        } ${className}`}
                        style={{
                            zIndex: open ? 101 : 100,
                            width: expandable && open ? "200%" : "",
                            ...style,
                        }}
                        tabIndex={0}
                        onFocus={handleFocus}
                        onMouseEnter={handleMouseEnter}
                    >
                        <motion.span
                            className="select-icon"
                            animate={{ rotate: open ? 180 : 0 }}
                            onClick={handleToggle}
                        >
                            <DownIcon />
                        </motion.span>

                        <main
                            className="select-selected"
                            ref={ref}
                            onClick={handleToggle}
                        >
                            {loading && <Loader small />}
                            {hasValue ? showValue : placeholder}
                        </main>
                        <AnimatePresence>
                            {open && (
                                <motion.section
                                    className={`select-options ${
                                        expandable
                                            ? "select-options-expandable"
                                            : ""
                                    }`}
                                    variants={anim.select}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                >
                                    <div>
                                        {loading && <Loader />}
                                        {!loading &&
                                            (Object.entries(options).length >
                                            0 ? (
                                                <>
                                                    {/* {searchable && (
														<Input
															ref={searchRef}
															value={search}
															placeholder="Start typing to search"
															onChange={(e) =>
																setSearch(
																	e.target
																		.value
																)
															}
														/>
													)}
													{nested
														? renderNestedOptions(
																options,
																search
														  )
														: renderOptions(
																options,
																search
														  )} */}
                                                    {renderOptions(
                                                        options,
                                                        search
                                                    )}
                                                </>
                                            ) : (
                                                <section className="select-no-options">
                                                    {emptyMessage}
                                                </section>
                                            ))}
                                    </div>
                                    {expandable && (
                                        <section
                                            className={`select-options-expandable-area ${
                                                independantExpandable
                                                    ? "select-options-expandable-independant-area"
                                                    : ""
                                            }`}
                                        >
                                            {!independantExpandable && (
                                                <>
                                                    <h2 className="select-options-expandable-area-title">
                                                        Preview Title
                                                    </h2>
                                                    <div className="select-options-expandable-area-body" />
                                                </>
                                            )}
                                            {independantExpandable &&
                                                renderExpandableArea(
                                                    mapExpandableAreaProps(
                                                        data.current,
                                                        highlightedKey
                                                    )
                                                )}
                                        </section>
                                    )}
                                </motion.section>
                            )}
                        </AnimatePresence>
                    </section>
                </>
            );
        }
    )
);

export const ImageSelector = memo(
    ({
        imgArray,
        className = "",
        selected = 0,
        onChange,
        direct = false,
        ...props
    }) => {
        if (!Array.isArray(imgArray) || imgArray.length < 1) {
            return;
        }

        const handleClick = useCallback(
            (value) => {
                if (!onChange || typeof onChange !== "function") {
                    return;
                }
                onChange(imgArray[value], value);
            },
            [onChange, imgArray]
        );

        return (
            <section className={`img-selector ${className}`} {...props}>
                {imgArray.map((imgItem, index) => (
                    <img
                        key={`${imgItem}_${index}`}
                        onClick={() => handleClick(index)}
                        className={`${
                            index === selected ? "img-selector-selected" : ""
                        }`}
                        src={direct ? imgItem : getImgUrl(imgItem, 50, 50)}
                        alt=""
                    />
                ))}
            </section>
        );
    }
);

export const RoundedCard = memo(
    forwardRef(({ className = "", children, ...props }, ref) => {
        return (
            <motion.section
                className={`rounded-card ${className}`}
                ref={ref}
                {...props}
            >
                {children}
            </motion.section>
        );
    })
);

export const ExpandableContainer = memo(
    ({
        className = "",
        children,
        expandOpenFooter,
        triggerExpand = false,
        disableExpandBtn = false,
        onExpand,
        showExpandThreshold,
        ...props
    }) => {
        const [expanded, setExpanded] = useState(false);

        const handleClick = useCallback(() => {
            setExpanded(true);
            if (onExpand && typeof onExpand === "function") {
                onExpand();
            }
        }, [onExpand]);

        useEffect(() => {
            if (!triggerExpand) {
                return;
            }
            handleClick();
        }, [triggerExpand, handleClick]);

        useEffect(() => {
            if (!showExpandThreshold || typeof children !== "string") {
                return;
            }
            if (children.length <= showExpandThreshold) {
                setExpanded(true);
            }
        }, [showExpandThreshold, children]);
        return (
            <section
                className={`expandable-container ${
                    expanded ? "expandable-container-expanded" : ""
                } ${className}`}
                {...props}
            >
                {children}
                {!expanded && (
                    <div
                        className="expandable-container-footer"
                        onClick={disableExpandBtn ? () => {} : handleClick}
                    >
                        {expandOpenFooter || "Learn More"}
                    </div>
                )}
            </section>
        );
    }
);

export const BadgeButton = memo(
    ({ className = "", badgeItem = false, children, ...props }) => (
        <div
            className={`badge-button ${className} ${
                badgeItem ? "badge-button-item" : ""
            }`}
            {...props}
        >
            {children}
        </div>
    )
);

export const InlineQuantityPicker = memo(
    ({
        className = "",
        quantity = 1,
        setQuantity,
        minQuantity = 1,
        maxQuantity = 5,
        ...props
    }) => {
        const handleChange = (add = false) => {
            if (!setQuantity || typeof setQuantity !== "function") {
                return;
            }
            if (
                (add && quantity === maxQuantity) ||
                (!add && quantity === minQuantity)
            ) {
                return;
            }
            setQuantity((quantity) => (add ? quantity + 1 : quantity - 1));
        };

        return (
            <section
                className={`inline-quantity-picker ${className}`}
                {...props}
            >
                <div
                    className={`inline-quantity-icon ${
                        quantity === minQuantity
                            ? "inline-quantity-icon-disabled"
                            : ""
                    }`}
                    onClick={() => handleChange(false)}
                >
                    <MinusIcon />
                </div>
                <div className="inline-quantity-text">{quantity}</div>
                <div
                    className={`inline-quantity-icon ${
                        quantity === maxQuantity
                            ? "inline-quantity-icon-disabled"
                            : ""
                    }`}
                    onClick={() => handleChange(true)}
                >
                    <PlusIcon />
                </div>
            </section>
        );
    }
);

export const PostPurchaseExtensionPreview = memo(
    ({ surfaceData, handleClose, className = "", type = "", ...props }) => {
        const [interactions, setInteractions] = useState({});

        useEffect(() => {
            document
                .querySelectorAll(`.${EXTENSION_PREVIEW_SKIP_BTN_CLASS}`)
                .forEach((btn) => btn.addEventListener("click", handleClose));

            return () => {
                document
                    .querySelectorAll(`.${EXTENSION_PREVIEW_SKIP_BTN_CLASS}`)
                    .forEach((btn) =>
                        btn.removeEventListener("click", handleClose)
                    );
            };
        }, [handleClose]);

        useEffect(() => {
            if (type !== "offer") return;
            setInteractions({ open: false });
            const handleClick = () => {
                setInteractions((interactions) => ({
                    ...interactions,
                    open: !interactions?.open,
                }));
            };
            const element = document.querySelector(
                `.${EXTENSION_OFFER_TOGGLE_CLASS}`
            );
            if (!element) return;
            element.addEventListener("click", handleClick);
            return () => {
                if (!element) return;
                element.addEventListener("click", handleClick);
            };
        }, [type]);

        const args = { ...surfaceData, interactions };

        return (
            <section
                className={`post-purchase-extension-preview ${className}`}
                {...props}
            >
                {parse(
                    type === "offer"
                        ? renderExtensionOffer(args)
                        : type === "reward"
                        ? renderExtensionReward(args)
                        : type === "survey"
                        ? renderExtensionSurvey(args)
                        : type === "custom_post"
                        ? renderExtensionCustomPost(args)
                        : ""
                )}
            </section>
        );
    }
);
